import React, { useState, useEffect } from 'react';

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight } from 'lucide-react';
import { ImageWithFallback } from './ImageWithFallback';
import './SeasonalHighlights.css';

function SeasonalHighlights() {
  const [seasonalDishes, setSeasonalDishes] = useState([]);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    determineSeason();
    fetchSeasonalDishes();
  }, []);

  useEffect(() => {
    if (currentSeason) {
      fetchSeasonalDishes();
    }
  }, [currentSeason]);

  const determineSeason = () => {
    const month = new Date().getMonth() + 1; // 1-12
    const day = new Date().getDate();
    let season = null;
    let title = '';
    let subtitle = '';
    let theme = '';

    if (month === 12 && day >= 20) {
      season = 'holiday';
      title = 'Holiday Favorites';
      subtitle = 'Festive dishes perfect for the season';
      theme = 'December 2025';
    } else if (month === 10 && day >= 25) {
      season = 'halloween';
      title = 'Spooky Specials';
      subtitle = 'Halloween-themed treats and dishes';
      theme = 'October 2025';
    } else if (month === 2 && day >= 10 && day <= 16) {
      season = 'valentines';
      title = "Valentine's Specials";
      subtitle = "Romantic dishes for Valentine's Day";
      theme = 'February 2025';
    } else if (month === 12 || month === 1 || month === 2) {
      season = 'winter';
      title = 'Winter Warmers';
      subtitle = 'Cozy comfort foods perfect for the cold season';
      theme = 'Winter 2025';
    } else if (month >= 3 && month <= 5) {
      season = 'spring';
      title = 'Spring Fresh';
      subtitle = 'Light and fresh dishes for spring';
      theme = 'Spring 2025';
    } else if (month >= 6 && month <= 8) {
      season = 'summer';
      title = 'Summer Favorites';
      subtitle = 'Refreshing dishes perfect for summer';
      theme = 'Summer 2025';
    } else {
      season = 'fall';
      title = 'Fall Flavors';
      subtitle = 'Rich and hearty autumn dishes';
      theme = 'Fall 2025';
    }

    setCurrentSeason({ season, title, subtitle, theme });
  };

  const fetchSeasonalDishes = async () => {
    try {
      setLoading(true);

      // Use mock data that matches the design
      const mockSeasonalDishes = [
        {
          id: '1',
          name: 'Butternut Squash Ravioli',
          restaurant: 'Bella Italia',
          image: 'https://images.unsplash.com/photo-1760905066154-429c734ca11b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzZWFzb25hbCUyMGZvb2QlMjBhdXR1bW4lMjBoYXJ2ZXN0fGVufDF8fHx8MTc2NDA5OTExMnww&ixlib=rb-4.1.0&q=80&w=1080',
          rating: 4.9,
          votes: 234,
        },
        {
          id: '2',
          name: 'Roasted Turkey Dinner',
          restaurant: 'Garden Fresh',
          image: 'https://images.unsplash.com/photo-1574672280600-4accfa5b6f98?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxyb2FzdCUyMHR1cmtleSUyMGRpbm5lcnxlbnwxfHx8fDE3NjQwOTkyMTB8MA&ixlib=rb-4.1.0&q=80&w=1080',
          rating: 4.8,
          votes: 198,
        },
        {
          id: '3',
          name: 'Gingerbread Tiramisu',
          restaurant: 'Sweet Endings',
          image: 'https://images.unsplash.com/photo-1571115177098-24ec42ed204d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxkZXNzZXJ0JTIwdGlyYW1pc3V8ZW58MXx8fHwxNzY0MDk5MjEwfDA&ixlib=rb-4.1.0&q=80&w=1080',
          rating: 5.0,
          votes: 312,
        },
        {
          id: '4',
          name: 'Winter Spice Cocktail',
          restaurant: 'The Mixology',
          image: 'https://images.unsplash.com/photo-1634058991875-3e883a00e9fc?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx3aW50ZXIlMjBjb2NrdGFpbCUyMGRyaW5rfGVufDF8fHx8MTc2NDA5OTIxMHww&ixlib=rb-4.1.0&q=80&w=1080',
          rating: 4.7,
          votes: 156,
        },
      ];

      setSeasonalDishes(mockSeasonalDishes);
    } catch (error) {
      console.error('Error fetching seasonal dishes:', error);
      setSeasonalDishes([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !currentSeason || seasonalDishes.length === 0) {
    return null;
  }

  return (
    <div className="border-t border-b border-border py-16 my-16 bg-stone-50">
      <div className="container mx-auto px-8 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-6 h-6" />
                <p className="text-xs uppercase tracking-widest text-muted-foreground">
                  {currentSeason.theme}
                </p>
              </div>
              <h2 className="serif text-6xl mb-3">{currentSeason.title}</h2>
              <p className="text-muted-foreground">{currentSeason.subtitle}</p>
            </div>
            <button className="flex items-center gap-2 text-xs uppercase tracking-widest hover:gap-3 transition-all">
              <span>View All</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </motion.div>

        <div className="grid grid-cols-4 gap-6">
          {seasonalDishes.map((dish, index) => (
            <motion.article
              key={dish.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-background border border-border group hover:border-foreground transition-colors cursor-pointer"
            >
              <Link to={`/dish/${dish.id}`} className="block">
                <div className="aspect-square overflow-hidden relative">
                  <ImageWithFallback
                    src={dish.image}
                    alt={dish.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-4 right-4 bg-background px-3 py-1.5">
                    <span className="text-xs">★ {dish.rating.toFixed(1)}</span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-sm mb-1 group-hover:text-muted-foreground transition-colors">
                    {dish.name}
                  </h3>
                  <p className="text-xs text-muted-foreground mb-2">{dish.restaurant}</p>
                  <p className="text-xs text-muted-foreground">{dish.votes} votes</p>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </div>
  );
}

export default SeasonalHighlights;
